import {gql} from '@apollo/client';
import {mapObject} from '@benchling/js-utils';
import React from 'react';
import isEqual from 'react-fast-compare';
import _ from 'underscore';

import * as analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import {REQUESTS_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {graphQLToREST} from 'graphql-client/declarative-translations/external/translate';
import {accessibleObjs, isInaccessible} from 'graphql-client/InaccessibleInfo';
import {RequestFullfillableStatuses} from 'requests/RequestConstants';
import {getContainerIds} from 'requests/SampleGroupUtil';
import {UIRequestTask} from 'requests/tasks/RequestTaskConstants';
import {PreparedUISampleGroup} from 'workflows/samples/PreparedSampleGroupConstants';

import {ContainerGraphQL} from '../../../graphql-fragments/inventory/__generated/ContainerGraphQL';
import {StorableSummaryGraphQL} from '../../../graphql-fragments/inventory/__generated/StorableSummaryGraphQL';
import {RequestTaskGraphQL} from '../../../graphql-fragments/requests/__generated/RequestTaskGraphQL';
import {RequestTaskSchemaGraphQL} from '../../../graphql-fragments/requests/schemas/__generated/RequestTaskSchemaGraphQL';
import {SampleGroupSchemaGraphQL} from '../../../graphql-fragments/requests/schemas/__generated/SampleGroupSchemaGraphQL';
import {
  EditableRequestSampleTableQuery,
  EditableRequestSampleTableQueryVariables,
} from './__generated/EditableRequestSampleTableQuery';
import EditingRequestSampleTable from './EditingRequestSampleTable';
import RequestSampleTable from './RequestSampleTable';

interface EditableRequestSampleTableProps {
  requestId?: string;
  canToggleEditMode: boolean;
  isWritable: boolean;
  isEditing: boolean;
  isSaving: boolean;
  requestTaskSchemasById: {[taskSchemaId: string]: RequestTaskSchemaGraphQL};
  requestTasks: Array<RequestTaskGraphQL | UIRequestTask>;
  sampleGroupIdsByRequestTaskId: {[taskId: string]: Array<string>};
  sampleGroupSchema: SampleGroupSchemaGraphQL;
  // We are populating cid of SampleGroups in DraftableRequestSampleTable, so that we could
  // identify them similarly to UISampleGroups. This is also the reason the callbacks take sampleGroupCid
  sampleGroups: Array<PreparedUISampleGroup>;
  fulfillableStatuses: RequestFullfillableStatuses;
  onCancelEdit: () => void;
  onEdit: () => void;
  onSave: () => void;
  onUpdateSampleGroups: (sampleGroups: Array<PreparedUISampleGroup>) => void;
  onUpdateSampleGroupIdsByRequestTaskId: (sampleGroupIdsByRequestTaskId: {
    [taskId: string]: Array<string>;
  }) => void;
}

interface QueryProps {
  containersById: {[containerId: string]: ContainerGraphQL};
  ancestralStorablesById: Record<string, Array<StorableSummaryGraphQL>>;
}

class EditableRequestSampleTable extends React.Component<EditableRequestSampleTableProps & QueryProps> {
  _handleBulkAddRows = (sampleGroups: Array<PreparedUISampleGroup>): void => {
    const newSampleGroups = [...this.props.sampleGroups, ...sampleGroups];
    this.props.onUpdateSampleGroups(newSampleGroups);
    for (let i = 0; i < newSampleGroups.length; i++) {
      analytics.deprecatedTrackEvent(REQUESTS_ANALYTICS_ACTIONS.ADDED_SAMPLE_GROUP);
    }
  };

  _handleBulkRemoveRows = (cids: Array<string>): void => {
    const newSampleGroups = this.props.sampleGroups.filter(({cid}) => !cids.includes(cid));
    this.props.onUpdateSampleGroups(newSampleGroups);
  };

  override render(): JSX.Element {
    if (this.props.isEditing) {
      return (
        <EditingRequestSampleTable
          canToggleEditMode={this.props.canToggleEditMode}
          isWritable={this.props.isWritable}
          isSaving={this.props.isSaving}
          requestId={this.props.requestId || null}
          requestTaskSchemasById={this.props.requestTaskSchemasById}
          requestTasks={this.props.requestTasks}
          sampleGroupIdsByRequestTaskId={this.props.sampleGroupIdsByRequestTaskId}
          sampleGroupSchema={this.props.sampleGroupSchema}
          sampleGroups={this.props.sampleGroups}
          containersById={this.props.containersById}
          ancestralStorablesById={this.props.ancestralStorablesById}
          fulfillableStatuses={this.props.fulfillableStatuses}
          onBulkAddRows={this._handleBulkAddRows}
          onBulkRemoveRows={this._handleBulkRemoveRows}
          onUpdateSampleGroupIdsByRequestTaskId={this.props.onUpdateSampleGroupIdsByRequestTaskId}
          onCancelEdit={this.props.onCancelEdit}
          onSave={this.props.onSave}
        />
      );
    } else {
      assert(this.props.requestId != null, 'Request ID is expected for readonly mode');
      return (
        <RequestSampleTable
          requestId={this.props.requestId}
          sampleGroupSchema={this.props.sampleGroupSchema}
          sampleGroups={this.props.sampleGroups}
          requestTasks={this.props.requestTasks}
          sampleGroupIdsByRequestTaskId={this.props.sampleGroupIdsByRequestTaskId}
          requestTaskSchemasById={this.props.requestTaskSchemasById}
          containersById={this.props.containersById}
          ancestralStorablesById={this.props.ancestralStorablesById}
          fulfillableStatuses={this.props.fulfillableStatuses}
          canToggleEditMode={this.props.isWritable && this.props.canToggleEditMode}
          onEdit={this.props.onEdit}
        />
      );
    }
  }
}

export const EDITABLE_REQUEST_SAMPLE_TABLE_QUERY = gql`
  query EditableRequestSampleTableQuery($containerIds: [StrictString!]!)
  @skipAudit
  @engComponent(name: REQUESTS) {
    containers(ids: $containerIds) {
      ... on ContainerNode {
        ...ContainerGraphQL
        parentStorable(skipAuthCheck: true) {
          ... on StorableInterface {
            ...StorableSummaryGraphQL
          }
          ...InaccessibleInfoGraphQL
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('Container').fragment}
  ${getFragmentWrapper('StorableSummary').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

function QueryingEditableRequestSampleTable(props: EditableRequestSampleTableProps): JSX.Element {
  const containerIds = getContainerIds(
    props.sampleGroups.map((sampleGroup) => ({
      sampleGroup,
      sampleGroupSchema: graphQLToREST('SampleGroupSchema', props.sampleGroupSchema),
    }))
  );

  const containersResult = useBenchlingQuery<
    EditableRequestSampleTableQuery,
    EditableRequestSampleTableQueryVariables
  >(EDITABLE_REQUEST_SAMPLE_TABLE_QUERY, {variables: {containerIds}, renderCachedDataWhenRefetching: true});

  let containersById: {[containerId: string]: ContainerGraphQL} = {};
  if (containersResult.type === QueryResultType.SUCCESS) {
    containersById = _.indexBy(accessibleObjs(containersResult.data.containers), ({id}) => id);
  }

  const ancestralStorablesById = mapObject(containersById, (container) => {
    const ancestralStorables: Array<StorableSummaryGraphQL> = [];
    let currentStorable: StorableSummaryGraphQL | null = container;

    while (currentStorable != null && !isInaccessible(currentStorable)) {
      ancestralStorables.push(currentStorable);
      currentStorable = currentStorable.parentStorable as StorableSummaryGraphQL;
    }

    return ancestralStorables;
  });

  return (
    <EditableRequestSampleTable
      {...props}
      containersById={containersById}
      ancestralStorablesById={ancestralStorablesById}
    />
  );
}

function excludeRequestTaskTagsFromProps(
  props: EditableRequestSampleTableProps
): EditableRequestSampleTableProps {
  const {requestTasks, ...otherProps} = props;
  return {
    ...otherProps,
    requestTasks: requestTasks.map((requestTask) => {
      if ((requestTask as UIRequestTask).tags) {
        const {tags, ...otherRequestTaskAttrs} = requestTask as UIRequestTask;
        return {...otherRequestTaskAttrs, tags: []};
      } else if ((requestTask as RequestTaskGraphQL).tagsJson) {
        const {tagsJson, ...otherRequestTaskAttrs} = requestTask as RequestTaskGraphQL;
        return {...otherRequestTaskAttrs, tagsJson: []};
      } else {
        return requestTask;
      }
    }),
  };
}

function areEqual(
  props: EditableRequestSampleTableProps,
  nextProps: EditableRequestSampleTableProps
): boolean {
  // Request task schema field values (request task tags) are not visible in EditableRequestSampleTable.
  // Exclude these tags from props comparison to avoid unnecessary re-rendering.
  return isEqual(excludeRequestTaskTagsFromProps(props), excludeRequestTaskTagsFromProps(nextProps));
}

export default React.memo(QueryingEditableRequestSampleTable, areEqual);
